import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/** One key per list that offers "Row per page". */
export type PageSizeKey =
  | 'bookings'
  | 'audit-log'
  | 'user-access.location'
  | 'user-access.employee'
  | 'closing-dates.location'
  | 'closing-dates.resource'
  | 'closing-dates.activity'
  | 'inventory.activity'
  | 'inventory.add-on'
  | 'inventory.resource'
  | 'inventory.food-and-beverage'
  | 'inventory.category'
  | 'inventory.smart-suggestion'
  | 'inventory.alternative-activity'
  | 'inventory.combo-deal'
  | 'pricing.coupon'
  | 'pricing.gift-card'
  | 'setting.tax'
  | 'setting.reseller'
  | 'setting.group-type'
  | 'setting.age-group-preset'
  | 'setting.group-size-preset'
  | 'setting.gift-card-value'
  | 'console.client'
  | 'console.tax'
  | 'console.flag-company'
  | 'console.fee-report';

export const DEFAULT_PAGE_SIZE = 10;

interface PageSizeState {
  sizes: Partial<Record<PageSizeKey, number>>;
  setPageSize: (key: PageSizeKey, size: number) => void;
}

/**
 * The page size each list was last shown at, so leaving a list and coming
 * back (or reloading) keeps it. A device preference, not an account one: it
 * survives login, logout and a company switch.
 */
export const usePageSizeStore = create<PageSizeState>()(
  persist(
    set => ({
      sizes: {},
      setPageSize: (key, size) => {
        if (!Number.isInteger(size) || size <= 0) return;
        set(state => ({ sizes: { ...state.sizes, [key]: size } }));
      },
    }),
    { name: 'page-size' }
  )
);

/**
 * The remembered size for a list, for seeding its filters. Read at call time,
 * so a reset built from it keeps the size the user picked.
 */
export const getPageSize = (
  key: PageSizeKey,
  fallback: number = DEFAULT_PAGE_SIZE
): number => {
  const size = usePageSizeStore.getState().sizes[key];
  return typeof size === 'number' && Number.isInteger(size) && size > 0
    ? size
    : fallback;
};
